import { cn } from "@/lib/utils";

interface SegmentedControlOption {
  value: string;
  label: string;
}

interface SegmentedControlProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SegmentedControlOption[];
  className?: string;
  size?: "default" | "sm";
}

/**
 * SegmentedControl - A lightweight mode selector that feels like configuration, not navigation.
 * 
 * Used for switching between modes within the same page (e.g., Smart Input vs Manual Entry).
 * Visually lighter than tabs to indicate these are not separate pages.
 */
export function SegmentedControl({
  value,
  onValueChange,
  options,
  className,
  size = "default",
}: SegmentedControlProps) {
  const isSmall = size === "sm";
  return (
    <div
      className={cn(
        "inline-flex items-center bg-muted/50 rounded-lg gap-0.5",
        isSmall ? "p-px" : "p-0.5",
        className
      )}
      role="radiogroup"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onValueChange(option.value)}
          className={cn(
            "font-medium rounded-md transition-[background-color,color] duration-200",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
            isSmall ? "px-2.5 py-1 text-xs" : "px-4 py-1.5 text-sm",
            value === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-background/50"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
